import { Stack } from '@mantine/core';
import { useUpdateEffect } from 'ahooks';
import { observer } from 'mobx-react-lite';
import type { MutableRefObject } from 'react';
import { useEffect, useReducer, useRef, useState } from 'react';
import { ThemeProvider as OutlineThemeProvider } from 'styled-components';
import type { ISecodaEditor } from '.';
import removeCommentMark from './commands/removeCommentMark';
import { BlockMenu } from './components/toolbars/BlockMenu';
import { EmojiMenu } from './components/toolbars/EmojiMenu';
import { FindAndReplace } from './components/toolbars/FindAndReplace';
import LinkToolbar from './components/toolbars/LinkToolbar';
import { PlaceholderToolbar } from './components/toolbars/PlaceholderToolbar';
import { SelectionToolbar } from './components/toolbars/SelectionToolbar';
import { TopGapButton } from './components/TopGapButton';
import { SecodaEditorContext } from './context';
import { COMMENT_PLACEHOLDER_ID } from './marks/Comment';
import { SecodaEditor } from './state';
import StyledEditor from './styles/editor';
import { light as lightOutlineTheme } from './styles/theme';
import { Toolbar, type SecodaEditorProps } from './types';

export type EditorProps = {
	style?: React.CSSProperties;
	className?: string;
	disableTopGap?: boolean;
	defaultValue?: string;
	value?: string;
	dataTestId?: string;
	editorRef?: MutableRefObject<ISecodaEditor | null>;
} & SecodaEditorProps;

export const Editor = observer((props: EditorProps) => {
	const [, forceUpdate] = useReducer((x) => x + 1, 0);
	if (props.value && (props.handleChange || !props.readOnly)) {
		throw new Error('Editor: value prop is only supported in readOnly mode');
	}

	const editorElementRef = useRef<HTMLDivElement>(null);
	const [editorState] = useState(
		() => new SecodaEditor(props, () => forceUpdate())
	);

	const {
		style,
		className,
		disableTopGap,
		readOnly,
		dataTestId,
		focusedCommentID,
		autoFocus,
		singleLineEditor,
		template,
		onClickComment,
	} = props;

	useEffect(() => {
		if (props.editorRef) {
			props.editorRef.current = editorState;
		}

		if (editorElementRef.current) {
			editorState.createView(
				editorElementRef.current,
				props.value || props.defaultValue || ''
			);
			editorState.setInitialFocus();
		}

		return () => {
			if (!editorState.view) {
				return;
			}

			removeCommentMark(
				editorState.view.state,
				editorState.view.dispatch,
				COMMENT_PLACEHOLDER_ID
			);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- we don't want to re-render the editor when the defaultValue changes
	}, [editorState, props.value, props.editorRef]);

	// React to updates to readOnly prop
	useUpdateEffect(() => {
		editorState.setReadOnly(props.readOnly);
	}, [props.readOnly]);

	return (
		<SecodaEditorContext.Provider value={editorState}>
			<Stack
				style={style}
				className={className}
				align="flex-start"
				justify="center"
			>
				<OutlineThemeProvider theme={lightOutlineTheme}>
					{!readOnly && !disableTopGap && <TopGapButton />}
					<StyledEditor
						ref={editorElementRef}
						id="documentation"
						data-testid={dataTestId ?? 'rich-text-editor'}
						focusedCommentID={focusedCommentID}
						readOnly={readOnly}
						readOnlyWriteCheckboxes={true}
						spellCheck={false}
						data-autofocus={autoFocus ? 'true' : undefined}
						singleLineEditor={singleLineEditor ? true : undefined}
						rtl={false}
					/>
					{editorState.view && (
						<>
							<SelectionToolbar
								isTemplate={!!template}
								isRestrictedToComments={!!readOnly}
								commentsEnabled={!!onClickComment}
								isOpen={editorState.openedToolbar === Toolbar.Selection}
								editorState={editorState}
							/>
							{editorState.openedToolbar === Toolbar.FindAndReplace && (
								<FindAndReplace
									editor={editorState}
									readOnly={!!readOnly}
									onClose={() =>
										editorState.closeToolbars(Toolbar.FindAndReplace)
									}
								/>
							)}
						</>
					)}
					{!readOnly && editorState.view && (
						<>
							{editorState.openedToolbar === Toolbar.Link && (
								<LinkToolbar
									editor={editorState}
									onClickLink={props.onClickLink}
								/>
							)}
							{editorState.openedToolbar === Toolbar.Placeholder && (
								<PlaceholderToolbar editor={editorState} />
							)}
							{editorState.openedToolbar === Toolbar.Emoji && (
								<EmojiMenu editor={editorState} />
							)}
							{editorState.openedToolbar === Toolbar.BlockMenu && (
								<BlockMenu editor={editorState} />
							)}
						</>
					)}
					{Array.from(editorState.renderers).map((view) => view.content)}
					{Object.values(editorState.widgets).map((Widget, index) => (
						<Widget
							// eslint-disable-next-line react/no-array-index-key
							key={index}
						/>
					))}
				</OutlineThemeProvider>
			</Stack>
		</SecodaEditorContext.Provider>
	);
});
